/**
 * Cash classification for a shift's ORDERS breakdown.
 *
 * The orders view (`database/ddl/views.sql`, `shift_sales`) exposes only
 * `payment_details->>'name'`, so there is no method id to match on here and
 * classification has to go through the name. This resolves that name against
 * the business's configured payment methods and reads their real `is_cash`,
 * instead of comparing against the literals 'Efectivo'/'Cash' — free text a
 * business owner types, which is how a method named "Contado" or
 * "Efectivo MXN" silently fell out of a whole day's cash total.
 *
 * Ported from desktop/utils/shiftCash.ts. Keep the two in step.
 */

export type CashNameLookup = (name: string | null | undefined) => boolean | null

/** `cash` is the total; `unclassified` counts rows the answer is not certain about. */
export type CashTotal = { cash: number, unclassified: number }

const normalizeName = (name: string | null | undefined): string =>
  typeof name === 'string' ? name.trim().toLowerCase() : ''

const parseAmount = (v: number | string | null | undefined): number => {
  if (v == null) return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : NaN
}

/**
 * Whether the configured methods can answer "is this name cash?" at all —
 * i.e. at least one NAMED method carries `is_cash === true`.
 */
export function hasConfiguredCashMethod(
  methods: Array<{ name?: string | null, is_cash?: boolean | null }>,
): boolean {
  return methods.some(m => normalizeName(m.name) !== '' && m.is_cash === true)
}

export function makeCashNameLookup(
  methods: Array<{ name?: string | null, is_cash?: boolean | null }>,
): CashNameLookup {
  // Every answer this table could give is `false`. Refuse to answer instead:
  // `is_cash` DEFAULTs to false, so a business that was never migrated has a
  // table full of falses, and answering from it reports zero cash for the day.
  if (!hasConfiguredCashMethod(methods)) return () => null

  const byName = new Map<string, boolean>()

  for (const m of methods) {
    const key = normalizeName(m.name)
    if (!key) continue
    // Two configured methods can share a name (rename collisions happen).
    // Any one of them flagged cash makes the name resolve as cash — erring
    // toward counting the money rather than silently dropping it.
    byName.set(key, (byName.get(key) ?? false) || m.is_cash === true)
  }

  return (name) => {
    const key = normalizeName(name)
    if (!key) return null
    const hit = byName.get(key)
    return hit === undefined ? null : hit
  }
}

/**
 * The two literals the store matched on before this module existed. Kept ONLY
 * as the fallback for a name the configured methods cannot resolve, so the
 * change is monotone: identical to the old behaviour when the lookup is
 * unavailable, correct when it is.
 */
const LEGACY_CASH_NAMES = new Set(['efectivo', 'cash'])

export function isLegacyCashName(name: string | null | undefined): boolean {
  return LEGACY_CASH_NAMES.has(normalizeName(name))
}

/**
 * Cash total over the orders breakdown. `unclassified` counts rows the
 * configured methods could not resolve (the legacy literals still decided
 * them, but the answer is a guess), rows where configuration and the legacy
 * name disagree, and rows whose amount is not a finite number — at most one
 * per row.
 */
export function cashSalesFromBreakdown(
  rows: Array<{ payment_type?: string | null, amount?: number | string | null }>,
  lookup: CashNameLookup,
): CashTotal {
  let cash = 0
  let unclassified = 0

  for (const row of rows) {
    const resolved = lookup(row.payment_type)
    const legacyCash = isLegacyCashName(row.payment_type)
    const isCashRow = resolved ?? legacyCash
    let failed = resolved == null || (resolved === false && legacyCash)

    if (isCashRow) {
      const amount = parseAmount(row.amount)
      if (Number.isNaN(amount)) failed = true
      else cash += amount
    }

    if (failed) unclassified += 1
  }

  return { cash, unclassified }
}

/**
 * Expected physical cash in the drawer at close.
 *
 * `cashDeposits` ADDS: a deposit is cash paid into the drawer, and the printed
 * ticket renders it with a leading `+`. Cash tips are deliberately NOT a term
 * — a check's total already includes its tip, so cash tips are inside
 * `cashSales` and adding them again would double-count.
 */
export function expectedCash(input: {
  openingCash: number
  cashSales: number
  cashDeposits: number
  cashExpenses: number
}): number {
  return input.openingCash + input.cashSales + input.cashDeposits - input.cashExpenses
}

/** Counted minus expected: negative is short, positive is over. */
export function cashDifference(counted: number, expected: number): number {
  return counted - expected
}

/**
 * Cash total over the CHECKS breakdown. Unlike the orders side this needs no
 * name lookup: the endpoint computes `is_cash` per row server-side, including
 * for split tenders.
 *
 * RELIABILITY NOTE: Unlike `cashSalesFromBreakdown`, this function cannot
 * report doubt — it silently treats missing/null `is_cash` flags and
 * unparseable amounts as zero contribution.
 *
 * The rows this function receives come from the `checks_tenders_by_shift`
 * view, which — as of the 2026-08-03 fix (see
 * `database/changes/checks_tenders_by_shift_v2_is_cash.md`) — resolves
 * `is_cash` against `payment_methods` rather than trusting a hardcoded or
 * synthesized flag. Before that fix the view's no-tender branch hardcoded
 * `is_cash: false`; after it, a `false` there falls back to the method's
 * current `is_cash`, and a `true` is trusted outright. So a total read
 * THROUGH the view — which is all this function ever sees — is reliable.
 *
 * That reliability does NOT extend to the raw `check_tenders` table if
 * anything ever reads it directly instead of through the view:
 * `server/repositories/checksRepository.ts` still synthesizes a tender row
 * with `is_cash: false` whenever a close payload arrives with no
 * `tenders[]` array — indistinguishable, at the row level, from a genuine
 * non-cash payment. The view launders that ambiguity by re-resolving
 * against the configured method; a query against `check_tenders` directly
 * would not.
 */
export function checksCashTotal(
  rows: Array<{ is_cash?: boolean | null, amount?: number | string | null }>,
): number {
  return rows.reduce((sum, row) => {
    if (row.is_cash !== true) return sum
    const amount = parseAmount(row.amount)
    return Number.isNaN(amount) ? sum : sum + amount
  }, 0)
}

/**
 * Which cash-figure warning a shift-close screen should show, or `null` for
 * none. Three input signals (see the doc comments on `computedCashMethodsLoaded`
 * / `computedCashMethodMissing` / `computedCashUnclassified` in
 * `stores/shared/shift.ts`), three outcomes:
 *
 * - `methodsLoaded === false` wins over everything else and always returns
 *   `null`. That covers TWO distinct cases this function cannot tell apart,
 *   and neither should show a warning: the catalogue simply has not arrived
 *   yet (a normal, temporary state early in the dialog's life), OR
 *   `usePaymentMethodsStore.fetchItems` already ran and its `catch` swallowed
 *   a network error, leaving `paymentMethods` permanently empty for the rest
 *   of this dialog's lifetime — nothing retries it. A genuinely failed fetch
 *   is therefore PERMANENTLY silent here, indistinguishable from one still in
 *   flight. That is a deliberate scope limit, not an oversight: adding a
 *   distinct "fetch failed" state (and any retry) is a store-level concern,
 *   left for whoever picks that up next. Showing either message here would
 *   blame a setup the business may have gotten right.
 * - `methodMissing` outranks `unclassifiedCount`: when the whole lookup
 *   table was rejected (no method flagged `is_cash`), every order row falls
 *   back to the legacy name match and gets counted as doubtful — so
 *   "N methods did not match your configured methods" would be false, and on
 *   a card-only business it would be false on every single close.
 * - Only once both of those are ruled out does an `unclassifiedCount > 0`
 *   surface its own message.
 *
 * NOTE: `unclassifiedMethods` reports doubt on the ORDERS side only.
 * `checksCashTotal` above cannot report doubt at all (see its RELIABILITY
 * NOTE) — so `unclassifiedCount === 0` here means "the orders half is fully
 * resolved", not "this whole figure is verified". Do not let calling code
 * imply otherwise.
 */
export type CashWarning = 'methodMissing' | 'unclassifiedMethods' | null

export function resolveCashWarning(input: {
  methodsLoaded: boolean
  methodMissing: boolean
  unclassifiedCount: number
}): CashWarning {
  if (!input.methodsLoaded) return null
  if (input.methodMissing) return 'methodMissing'
  if (input.unclassifiedCount > 0) return 'unclassifiedMethods'
  return null
}

/**
 * The four things the close screen can tell a cashier about the drawer, and
 * the only place that decision is made.
 *
 * `balanced` is a BAND, not an equality: expected cash is a sum of parsed
 * decimals, so a drawer that is right to the cent can still land a whisker off
 * zero. Half a cent is below anything anyone can count, and telling someone
 * they are $0.004 short would be worse than useless.
 *
 * The sign convention matches `cashDifference`: negative is a shortfall, the
 * case that costs the cashier money and the one an inverted subtraction would
 * silently turn into a surplus.
 */
export type CashCountState = 'empty' | 'balanced' | 'short' | 'over'

export function resolveCountState(typed: number | null): CashCountState {
  if (typed === null || !Number.isFinite(typed)) return 'empty'
  if (Math.abs(typed) < 0.005) return 'balanced'
  return typed < 0 ? 'short' : 'over'
}

/**
 * Both sales sources merged by method name, each with its share of the total.
 *
 * Lives here rather than in a component because two screens draw it — the
 * shift page and the close dialog — and a per-method breakdown that disagrees
 * between them is worse than one that is merely wrong. The total is summed
 * from the rows returned, so a caller that renders these rows and this total
 * cannot show a mismatch.
 */
export function salesByMethod(
  rows: Array<{ payment_type?: string | null, amount?: number | string | null }>,
  unknownLabel: string,
): { methods: Array<{ label: string, amount: number, pct: number }>, total: number } {
  const byType = new Map<string, number>()
  for (const row of rows) {
    const key = row.payment_type || unknownLabel
    const amount = parseAmount(row.amount)
    byType.set(key, (byType.get(key) ?? 0) + (Number.isNaN(amount) ? 0 : amount))
  }
  const total = [...byType.values()].reduce((sum, v) => sum + v, 0)
  return {
    total,
    methods: [...byType.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, amount]) => ({
        label,
        amount,
        pct: total > 0 ? Math.round((amount / total) * 100) : 0,
      })),
  }
}

/**
 * The difference for a counted amount as the cashier TYPES it, or null when
 * nothing usable has been typed yet.
 *
 * Exists because the obvious alternative bit once: the close dialog binds its
 * input to a local ref while `computedDiffCashAmount` on the store reads the
 * store's own `closingCashAmount`, which that binding never writes. The screen
 * showed "the drawer matches" for a $200 count against $100 expected, because
 * the difference was being taken against an empty value the whole time.
 *
 * Takes the raw input value rather than a number so the empty-vs-zero
 * distinction survives: '' means nothing typed, '0' means a counted empty
 * drawer, and those must not render the same.
 */
export function typedCashDifference(
  typed: string | number | null | undefined,
  expected: number,
): number | null {
  const raw = String(typed ?? '').trim()
  if (raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  return cashDifference(n, expected)
}
